import React, { useEffect, useState } from 'react'
import { useAppData } from '../../../../../../context/AppDataContext'
import VoucherInItemForm from './VoucherInItemForm';
import ConfirmDelete from '../../../../../../components/ConfirmDelete';
import { useAlert } from '../../../../../../context/AlertContext';
import { NumberToWords } from './NumberToWords';
import { getGstBreakup } from '../gstHelper';
import voucherInItemServices from '../voucherInItemServices';
import VoucherTaxSummary from './VoucherTaxSummary';

function VoucherInItemList({ voucherDetails, productList, isPrinting, companyDetails }) {
    const { getMasterLabel } = useAppData();
    const { AppAlert } = useAlert();

    // Primary Products States
    const [primarySubTotal, setPrimarySubTotal] = useState(0);
    const [primaryGstTotal, setPrimaryGstTotal] = useState(0);

    // Secondary Charges / Services States
    const [secondarySubTotal, setSecondarySubTotal] = useState(0);
    const [secondaryGstTotal, setSecondaryGstTotal] = useState(0);

    // Consolidated Final Footer States
    const [roundOff, setRoundOff] = useState(0);
    const [finalGrandTotal, setFinalGrandTotal] = useState(0);

    const [itemList, setItemList] = useState([]);

    // State to toggle secondary items section if it's currently empty
    const [showSecondarySection, setShowSecondarySection] = useState(false);

    const getItemList = () => {
        voucherInItemServices.getByVoucherId(voucherDetails.id).then(res => {
            setItemList(res.data);
        }).catch(error => {
            console.error("Error fetching items", error);
        });
    }

    useEffect(() => {
        getItemList();
    }, [voucherDetails])

    const calculateTotal = () => {
        if (itemList) {
            let _primarySub = 0;
            let _primaryGst = 0;
            let _secondarySub = 0;
            let _secondaryGst = 0;

            itemList.forEach(_item => {
                if (_item.isSecondary) {
                    _secondarySub += Number(_item.amount || 0);
                    _secondaryGst += Number(_item.gstAmount || 0);
                } else {
                    _primarySub += Number(_item.amount || 0);
                    _primaryGst += Number(_item.gstAmount || 0);
                }
            });

            setPrimarySubTotal(_primarySub.toFixed(2));
            setPrimaryGstTotal(_primaryGst.toFixed(2));

            setSecondarySubTotal(_secondarySub.toFixed(2));
            setSecondaryGstTotal(_secondaryGst.toFixed(2));

            const combinedRawTotal = _primarySub + _primaryGst + _secondarySub + _secondaryGst;
            const roundedTotal = Math.round(combinedRawTotal);
            const _roundOff = roundedTotal - combinedRawTotal;

            setRoundOff(_roundOff.toFixed(2));
            setFinalGrandTotal(roundedTotal);
        }
    }

    useEffect(() => {
        calculateTotal();
    }, [itemList])

    const deleteItem = (id) => {
        voucherInItemServices.remove(id).then(res => {
            AppAlert("Purchase item removed and inventory adjusted");
            getItemList();
        }).catch(error => {
            AppAlert(error.msg || "Error removing item", "error")
        })
    }

    const totalGstCombined = Number(primaryGstTotal) + Number(secondaryGstTotal);
    const taxRows = getGstBreakup(
        totalGstCombined,
        companyDetails?.state, 
        //voucherDetails?.billingAddress?.supplyState 
        voucherDetails?.supplyState 
    );

    const primaryItems = itemList.filter(item => !item.isSecondary);
    const secondaryItems = itemList.filter(item => item.isSecondary);

    const hasSecondaryItems = secondaryItems.length > 0;

    // Determine if the secondary section block itself should render
    const displaySecondaryBlock = hasSecondaryItems || (!isPrinting && voucherDetails.status === 1 && showSecondarySection);

    return (
        itemList && <>
            <table className='table table-sm table-striped table-bordered mb-0' style={{ fontSize: 12 }}>
                <thead className='table-dark'>
                    <tr>
                        <th>#</th>
                        <th>Item Description</th>
                        <th>HSN</th>
                        <th>Qty</th>
                        <th>UOM</th>
                        <th>Rate</th>
                        <th>GST%</th>
                        <th>Taxable Amt</th>
                    </tr>
                </thead>
                
                {/* SECTION 1: PRIMARY PRODUCTS */}
                <tbody>
                    {hasSecondaryItems && (
                        <tr className="table-light">
                            <td colSpan={8} className="fw-bold text-primary text-uppercase tracking-wide" style={{ letterSpacing: '0.5px' }}>
                                Primary Products / Core Items
                            </td>
                        </tr>
                    )}
                    {primaryItems.map((item, i) => (
                        <tr key={`primary-${i}`} className="hover-container">
                            <td>{i + 1}</td>
                            <td>
                                <div className="d-flex justify-content-between">
                                    <div>
                                        <div className='fw-bold'>{item.title}</div>
                                        <div className='ms-2'>{item.details}</div>
                                    </div>
                                    <div>
                                        {voucherDetails.status === 1 && (
                                            <ConfirmDelete id={item.id} confirm={deleteItem}>
                                                Remove {item.title} from purchase and reverse inventory?
                                            </ConfirmDelete>
                                        )}
                                    </div>
                                </div>
                            </td>
                            <td>{item.hsn}</td>
                            <td>{item.qty}</td>
                            <td>{getMasterLabel("product-uom", item.uom)}</td>
                            <td>{voucherDetails.voucherType === "Purchase" ? item.price : ""}</td>
                            <td>{voucherDetails.voucherType === "Purchase" ? item.gstRate : ""}</td>
                            <td>{voucherDetails.voucherType === "Purchase" ? item.amount : ""}</td>
                        </tr>
                    ))}
                    
                    {!isPrinting && voucherDetails.status === 1 && (
                        <tr>
                            <td colSpan={8} className="p-0">
                                <VoucherInItemForm
                                    voucherDetails={voucherDetails} 
                                    callBack={getItemList} 
                                    productList={productList} 
                                    isSecondary={false}
                                />
                            </td>
                        </tr>
                    )}
                    {/* Intermediate Primary Product Total - Hidden if no secondary items present */}
                    {voucherDetails.voucherType === "Purchase" && hasSecondaryItems && (
                        <tr className="table-warning-subtle text-dark">
                            <td colSpan={7} className="text-end fw-bold fst-italic">Total (Excl. Tax):</td>
                            <td className="fw-bold">{primarySubTotal}</td>
                        </tr>
                    )}
                </tbody>

                {/* OPTIONAL BUTTON: Appears only when empty and not editing/printing */}
                {!hasSecondaryItems && !showSecondarySection && !isPrinting && voucherDetails.status === 1 && (
                    <tbody>
                        <tr>
                            <td colSpan={8} className="text-center py-2 bg-light">
                                <button 
                                    type="button" 
                                    className="btn btn-sm btn-outline-success fw-bold"
                                    onClick={() => setShowSecondarySection(true)}
                                >
                                    + Add Extra Charges & Services (Logistics, Loading, etc.)
                                </button>
                            </td>
                        </tr>
                    </tbody>
                )}

                {/* SECTION 2: SECONDARY CHARGES & SERVICES */}
                {displaySecondaryBlock && (
                    <tbody>
                        <tr className="table-light">
                            <td colSpan={8} className="fw-bold text-success text-uppercase tracking-wide" style={{ letterSpacing: '0.5px' }}>
                                Extra Charges & Services (Logistics, Loading, Consumables)
                            </td>
                        </tr>
                        {secondaryItems.map((item, i) => (
                            <tr key={`secondary-${i}`} className="hover-container">
                                <td>{i + 1}</td>
                                <td>
                                    <div className="d-flex justify-content-between">
                                        <div>
                                            <div className='fw-bold'>{item.title}</div>
                                            <div className='ms-2'>{item.details}</div>
                                        </div>
                                        <div>
                                            {voucherDetails.status === 1 && (
                                                <ConfirmDelete id={item.id} confirm={deleteItem}>
                                                    Remove {item.title} from purchase and reverse inventory?
                                                </ConfirmDelete>
                                            )}
                                        </div>
                                    </div>
                                </td>
                                <td>{item.hsn}</td>
                                <td>{item.qty}</td>
                                <td>{getMasterLabel("product-uom", item.uom)}</td>
                                <td>{voucherDetails.voucherType === "Purchase" ? item.price : ""}</td>
                                <td>{voucherDetails.voucherType === "Purchase" ? item.gstRate : ""}</td>
                                <td>{voucherDetails.voucherType === "Purchase" ? item.amount : ""}</td>
                            </tr>
                        ))}
                        
                        {!isPrinting && voucherDetails.status === 1 && (
                            <tr>
                                <td colSpan={8} className="p-0">
                                    <VoucherInItemForm
                                        voucherDetails={voucherDetails} 
                                        callBack={getItemList} 
                                        productList={productList} 
                                        isSecondary={true}
                                    />
                                </td>
                            </tr>
                        )}
                        {/* Intermediate Secondary Charges Total - Only visible if there are elements */}
                        {voucherDetails.voucherType === "Purchase" && hasSecondaryItems && (
                            <tr className="table-light">
                                <td colSpan={7} className="text-end fw-bold fst-italic">Extra Total (Excl. Tax):</td>
                                <td className="fw-bold">{secondarySubTotal}</td>
                            </tr>
                        )}
                    </tbody>
                )}

                {/* GLOBAL FINAL SUMMARY FOOTER */}
                {voucherDetails.voucherType === "Purchase" && (
                    <tfoot>
                        <tr className="table-secondary">
                            <th colSpan={7} className='text-end fw-bold'>
                                {hasSecondaryItems ? "Total Taxable Value (Combined):" : "Total Taxable Value"}
                            </th>
                            <th className="fw-bold">{(Number(primarySubTotal) + Number(secondarySubTotal)).toFixed(2)}</th>
                        </tr>
                        
                        {voucherDetails.supplyState ? (
                            taxRows.map((row, index) => (
                                <tr key={index}>
                                    <th colSpan={7} className='text-end fst-italic'>
                                        {row.label} (Input)
                                    </th>
                                    <th>{row.amount}</th>
                                </tr>
                            ))
                        ) : (
                            <tr>
                                <td colSpan={8} className="text-center text-danger small">
                                    Select Supplier Address to see GST Breakup
                                </td>
                            </tr>
                        )}

                        {Number(roundOff) !== 0 && (
                            <tr>
                                <th colSpan={7} className='text-end fst-italic'>Round Off</th>
                                <th>{roundOff}</th>
                            </tr>
                        )}
                        
                        <tr className='table-primary'>
                            <th colSpan={7} className='text-end fw-bold'>
                                {hasSecondaryItems ? "Grand Total (Inc. All Items & Taxes)" : "Grand Total (Purchase Value)"}
                            </th>
                            <th style={{ fontSize: 14 }} className="fw-bold">
                                {finalGrandTotal.toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                            </th>
                        </tr>
                        
                        <tr>
                            <td colSpan={8}>
                                <div className='small'>Total Amount in Words:</div>
                                <div className='fw-bold'><NumberToWords amount={finalGrandTotal} /> Only</div>
                            </td>
                        </tr>
                    </tfoot>
                )}
            </table>

            {voucherDetails.voucherType === "Purchase" &&
                <VoucherTaxSummary itemList={itemList} />
            }
        </>
    )
}

export default VoucherInItemList;